"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { queryClient } from "@/config/react-query";
import { Icd10DescriptionMap, buildIcd10DescriptionMap, normalizeIcd10Codes } from "@/lib/billing/icd10-descriptions";
import { medicalCodeQueryOptions } from "@/api/medical-codes-query";

/**
 * Reference descriptions for the ICD-10 codes on a document, fetched reactively
 * so they're in hand when the document is built.
 *
 * Same constraint as `useModifierDescriptions`: the Super Bill's print path
 * builds its HTML *synchronously*, because `window.open` only succeeds while
 * the click's transient user activation is live, so the print handler cannot
 * await a fetch. The codes on the bill drive a cached query as they change and
 * the handler reads the resolved map; a map that hasn't loaded prints the bare
 * codes, exactly as the bill did before diagnoses were labelled.
 *
 * @param codes - Every ICD-10 code on the document, in any order, with
 *   duplicates and blanks allowed.
 */
export const useIcd10Descriptions = codes => {
  const $ = _c(11);
  let normalized;
  let t0;
  let t1;
  if ($[0] !== codes) {
    normalized = normalizeIcd10Codes(codes);
    t1 = useQuery;
    t0 = medicalCodeQueryOptions.icd10Descriptions(normalized);
    $[0] = codes;
    $[1] = normalized;
    $[2] = t0;
    $[3] = t1;
  } else {
    normalized = $[1];
    t0 = $[2];
    t1 = $[3];
  }
  const t2 = normalized.length > 0;
  let t3;
  if ($[4] !== t0 || $[5] !== t2) {
    t3 = {
      ...t0,
      enabled: t2,
      placeholderData: keepPreviousData,
      staleTime: Infinity
    };
    $[4] = t0;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data: icd10Codes
  } = t1(t3);
  let t4;
  if ($[7] !== icd10Codes) {
    t4 = icd10Codes ?? [];
    $[7] = icd10Codes;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = buildIcd10DescriptionMap(t4);
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};

/**
 * Resolve ICD-10 descriptions imperatively, for callers that CAN wait.
 *
 * The synchronous-HTML constraint applies only to printing. Emailing a Super
 * Bill is already async, so it must await this rather than reuse whatever the
 * reactive hook happens to hold — an emailed bill goes to the patient or payer
 * and cannot be corrected afterwards.
 */
export const fetchIcd10Descriptions = async (codes: string[]): Promise<Icd10DescriptionMap> => {
  const normalized = normalizeIcd10Codes(codes);
  if (normalized.length === 0) return {};
  try {
    const icd10Codes = await queryClient.fetchQuery(medicalCodeQueryOptions.icd10Descriptions(normalized));
    return buildIcd10DescriptionMap(icd10Codes ?? []);
  } catch {
    // Descriptions are a nicety; never block the send on them.
    return {};
  }
};